import { gql } from '@apollo/client';
import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Link } from '../utils/router/v3Bridge';
import Breadcrumbs from '../components/Breadcrumbs';
import ContentPreviewPopOver from '../components/ContentPreviewPopOver';
import type {
  TemplateRow_TemplateFragment,
  TemplateRow_TemplatePostFragment,
} from '../graphql/generated/graphqlTypes';
import PostDetailPreview from '../post/PostDetailPreview';
import nonEmpty from '../utils/nonEmpty';
import { createPostPath } from '../utils/url/postPath';
import templatePath, { savePreviousRoute } from '../utils/url/templatesPath';
import TemplateIcon from './TemplateIcon';
import styles from './TemplateRow.module.scss';

const TEMPLATE_ROW_TEMPLATE_FRAGMENT = gql`
  fragment TemplateRow_template on Template {
    id
    title
    topicOwner {
      id
      name
      ancestors {
        id
        name
      }
    }
    ...templatePath_template
  }
  ${templatePath.fragments.template}
`;

const TEMPLATE_ROW_TEMPLATE_POST_FRAGMENT = gql`
  fragment TemplateRow_templatePost on Post {
    id
    title
    topics {
      id
      name
      ancestors {
        id
        name
      }
    }
    ...templatePath_post
  }
  ${templatePath.fragments.post}
`;

const getTemplateBreadcrumbsItems = (
  templateTopic: TemplateRow_TemplateFragment['topicOwner'],
  currentTopicId?: string,
) => {
  if (!templateTopic) {
    return [];
  }

  const currentTopicInAncestorsIndex = currentTopicId
    ? templateTopic.ancestors?.findIndex(
        (ancestor) => ancestor?.id === currentTopicId,
      ) ?? -1
    : templateTopic.ancestors?.length ?? -1;

  if (currentTopicInAncestorsIndex === -1) {
    return [];
  }

  // Take ancestors only up to current topic (excluding).
  const ancestorsToBreadcrumbs =
    templateTopic.ancestors?.slice(0, currentTopicInAncestorsIndex).reverse() ??
    [];

  return [...ancestorsToBreadcrumbs, templateTopic]
    .map((topic) => (topic?.name ? { name: topic.name } : null))
    .filter(nonEmpty);
};

interface TemplateRowProps {
  template: TemplateRow_TemplateFragment | TemplateRow_TemplatePostFragment;
  currentTopicId?: string;
  link?: 'create' | 'detail' | null;
  showHoverCard?: boolean;
  padding?: 'regular' | 'small';
  hoverCardPlacement?: Placement;
}

const TemplateRow = ({
  template,
  currentTopicId,
  link = null,
  showHoverCard = false,
  padding = 'regular',
  hoverCardPlacement = 'left',
}: TemplateRowProps) => {
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);

  const topic = (() => {
    if (template.__typename === 'Template') {
      return template.topicOwner;
    }

    if (template.__typename === 'Post' && template.topics.length > 0) {
      return template.topics[0];
    }

    return null;
  })();

  const breadcrumbsItems = getTemplateBreadcrumbsItems(topic, currentTopicId);

  const view = (
    <>
      <TemplateIcon />
      <div className={styles.content}>
        <div className={styles.breadcrumbs}>
          <Breadcrumbs items={breadcrumbsItems} truncate />
        </div>
        <div
          className={classNames(styles.title, {
            [styles.titleOneLine]: breadcrumbsItems.length > 0,
            [styles.titleTwoLines]: breadcrumbsItems.length === 0,
          })}
        >
          {template.title}
        </div>
      </div>
    </>
  );

  const rowClassName = classNames(styles.row, {
    [styles.small]: padding === 'small',
  });

  const handleOnPreviewLoadChange = (loaded: boolean) => {
    setIsPreviewLoaded(loaded);
  };

  const row = link ? (
    <Link
      className={rowClassName}
      to={
        link === 'create'
          ? createPostPath({ templateId: template.id })
          : templatePath(template)
      }
      onClick={link === 'detail' ? () => savePreviousRoute() : undefined}
    >
      {view}
    </Link>
  ) : (
    <div className={rowClassName}>{view}</div>
  );

  if (showHoverCard) {
    return (
      <ContentPreviewPopOver
        hidden={!isPreviewLoaded}
        display="inline-block"
        popOver={
          <PostDetailPreview
            templateId={template.id}
            onPreviewLoadChange={handleOnPreviewLoadChange}
            onClick={() => savePreviousRoute()}
          />
        }
        placement={hoverCardPlacement}
      >
        {row}
      </ContentPreviewPopOver>
    );
  }

  return row;
};

TemplateRow.fragments = {
  template: TEMPLATE_ROW_TEMPLATE_FRAGMENT,
  templatePost: TEMPLATE_ROW_TEMPLATE_POST_FRAGMENT,
};

export default TemplateRow;
